import type { MemberPermissions, PageKey, Role } from '~~/types/rbac'

/**
 * Shape returned by `GET /api/me` (see `server/api/me.get.ts`).
 *
 * Admins receive the full page catalog and `allSchedules: true` with an empty
 * `scheduleIds`, meaning "every schedule is permitted". Non-admins receive
 * their explicit page grants and assignment list with `allSchedules: false`.
 */
interface MeResponse {
  role: Role
  pages: PageKey[]
  scheduleIds: string[]
  allSchedules: boolean
  isAdmin: boolean
}

/**
 * The reactive surface exposed by {@link usePermissions}. Extends the shared
 * {@link MemberPermissions} contract with schedule access, an "all schedules"
 * flag, loading/error state, and lifecycle helpers.
 */
export interface UsePermissionsReturn {
  role: ComputedRef<Role>
  pages: ComputedRef<Set<PageKey>>
  scheduleIds: ComputedRef<Set<string>>
  isAdmin: ComputedRef<boolean>
  allSchedules: ComputedRef<boolean>
  /** True while a `/api/me` request is in flight. */
  pending: Ref<boolean>
  /** Set when the most recent load failed. */
  error: Ref<unknown>
  /** True once permissions have been resolved at least once. */
  loaded: ComputedRef<boolean>
  /** Admins may see every page; otherwise the page must be in `pages`. */
  canSeePage: (key: PageKey) => boolean
  /** Admins (and "all schedules") may access any schedule; else it must be assigned. */
  canAccessSchedule: (id: string) => boolean
  /** Loads `/api/me` once (no-op if already loaded). Pass `force` to bypass the cache. */
  load: (force?: boolean) => Promise<void>
  /** Forces a fresh `/api/me` fetch. Use after an access change is expected. */
  refresh: () => Promise<void>
  /** Clears the cached permissions so the next `load()` re-fetches. */
  invalidate: () => void
}

/**
 * Loads and caches the current member's resolved access from `GET /api/me`.
 *
 * The permissions are fetched once after authentication and shared across the
 * app via `useState`, so every caller observes the same reactive snapshot. When
 * the authenticated user changes, the cache is reset and (if signed in)
 * re-fetched. Because an admin can change a member's access while they are
 * signed in — and that change takes effect on the member's *next request* (Req
 * 2.2, 3.2, 3.3, 5.4) — {@link UsePermissionsReturn.refresh} / `invalidate` let
 * callers force a re-fetch when an access change is expected.
 *
 * Unauthenticated callers resolve to an empty, locked-down permission set:
 * `role: 'user'`, no pages, no schedules, `isAdmin: false`.
 */
export function usePermissions(): UsePermissionsReturn {
  const user = useSupabaseUser()

  // `useRequestFetch()` returns a `$fetch` that forwards the incoming request's
  // headers (notably the auth cookies) during SSR. Plain `$fetch` does NOT, so
  // a server-side call to `/api/me` would be seen as unauthenticated — which
  // caused permissions to resolve as "logged out" on the server and then flip
  // on the client after hydration (the flaky 500 / empty-sidebar-until-refresh
  // symptom). On the client this is just the normal `$fetch`.
  const requestFetch = useRequestFetch()

  // Shared, app-wide caches keyed so every component reads the same snapshot.
  const state = useState<MeResponse | null>('permissions:state', () => null)
  const pending = useState<boolean>('permissions:pending', () => false)
  const error = useState<unknown>('permissions:error', () => null)
  const hasLoaded = useState<boolean>('permissions:loaded', () => false)

  async function load(force = false): Promise<void> {
    // Already cached and not forcing a refresh: nothing to do.
    if (hasLoaded.value && state.value && !force) return

    // Avoid overlapping in-flight requests.
    if (pending.value) return

    pending.value = true
    error.value = null
    try {
      // Always ask the server — `/api/me` authenticates via the request cookie
      // and returns 401 when genuinely logged out. We deliberately do NOT gate
      // on the client-side `useSupabaseUser()` ref: it can be momentarily (or
      // persistently) null on the client even when the session cookie is valid
      // server-side, which previously left permissions unresolved and the nav
      // empty. The cookie-forwarding `requestFetch` keeps this authenticated
      // during SSR too.
      state.value = await requestFetch<MeResponse>('/api/me')
    }
    catch (err) {
      // 401/transient failure → locked-down (no-access) state.
      state.value = null
      error.value = err
    }
    finally {
      pending.value = false
      hasLoaded.value = true
    }
  }

  /** Force a fresh fetch (e.g. after an admin changes this member's access). */
  function refresh(): Promise<void> {
    return load(true)
  }

  /** Drop the cache so the next `load()` re-fetches from the server. */
  function invalidate(): void {
    state.value = null
    hasLoaded.value = false
  }

  // Re-resolve when the authenticated user changes (sign in / sign out / switch).
  // `immediate` triggers the initial load once after auth is known.
  watch(
    user,
    (current, previous) => {
      if (current?.id !== previous?.id) {
        // Identity changed: drop any stale snapshot before reloading.
        state.value = null
        hasLoaded.value = false
      }
      void load()
    },
    { immediate: true },
  )

  const role = computed<Role>(() => state.value?.role ?? 'user')
  const isAdmin = computed<boolean>(() => state.value?.isAdmin ?? false)
  const allSchedules = computed<boolean>(() => state.value?.allSchedules ?? false)
  const pages = computed<Set<PageKey>>(() => new Set(state.value?.pages ?? []))
  const scheduleIds = computed<Set<string>>(() => new Set(state.value?.scheduleIds ?? []))
  const loaded = computed<boolean>(() => hasLoaded.value)

  /** Admins implicitly hold the full catalog; others must have an explicit grant. */
  function canSeePage(key: PageKey): boolean {
    if (isAdmin.value) return true
    return pages.value.has(key)
  }

  /** Admins / "all schedules" members can access anything; others need an assignment. */
  function canAccessSchedule(id: string): boolean {
    if (isAdmin.value || allSchedules.value) return true
    return scheduleIds.value.has(id)
  }

  return {
    role,
    pages,
    scheduleIds,
    isAdmin,
    allSchedules,
    pending,
    error,
    loaded,
    canSeePage,
    canAccessSchedule,
    load,
    refresh,
    invalidate,
  }
}

export default usePermissions
